import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Calendar, MapPin, Mail, Phone, User, Download, Home, Wallet, MessageCircle } from "lucide-react";
import { useData } from "../context/DataContext";
import { formatCurrency, formatDate, formatDateTime } from "../lib/format";

const BookingConfirmation = () => {
    const { bookingId } = useParams();
    const { fetchBookingById, settings } = useData();
    const [booking, setBooking] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetchBookingById(bookingId)
            .then((b) => !cancelled && setBooking(b))
            .catch((e) => !cancelled && setError(e?.response?.data?.detail || "Booking not found."));
        return () => {
            cancelled = true;
        };
    }, [bookingId, fetchBookingById]);

    const handleDownload = () => {
        const data = JSON.stringify(booking, null, 2);
        const blob = new Blob([data], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${booking.id}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };
    const handlePrint = () => window.print();

    if (error) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center text-center">
                <div>
                    <p className="text-ink-light">{error}</p>
                    <Link to="/" className="mt-4 inline-block text-blue-500 font-semibold">Back to home</Link>
                </div>
            </div>
        );
    }
    if (!booking) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center text-center text-ink-light">
                Loading booking…
            </div>
        );
    }

    const whatsappLink = `https://wa.me/${(settings?.whatsapp || "").replace(/[^\d]/g, "")}?text=${encodeURIComponent(
        `Hi SouthTrip, I just booked ${booking.id}. Looking forward to my Kerala trip!`,
    )}`;

    return (
        <div className="bg-cream-100 min-h-screen py-12" data-testid="confirmation-page">
            <div className="max-w-3xl mx-auto px-4 sm:px-6">
                <div className="bg-white rounded-2xl border border-cream-200 overflow-hidden shadow-sm">
                    <div className="bg-blue-500 text-white p-8 text-center">
                        <div className="w-16 h-16 mx-auto rounded-full bg-white flex items-center justify-center text-blue-500 mb-3 shadow-md">
                            <CheckCircle2 className="w-9 h-9" />
                        </div>
                        <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">Booking confirmed!</h1>
                        <p className="text-white/85 mt-1.5 text-sm">A confirmation email has been sent to {booking.customer.email}.</p>
                        <div className="mt-5 inline-block bg-white/15 backdrop-blur border border-white/30 rounded-xl px-5 py-2.5">
                            <div className="text-[10px] uppercase tracking-wider opacity-80">Booking ID</div>
                            <div className="font-mono text-lg font-semibold mt-0.5" data-testid="confirmation-booking-id">{booking.id}</div>
                        </div>
                    </div>

                    <div className="p-6 lg:p-8 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <img src={booking.cabImage || booking.packageImage} alt="" className="w-full h-32 md:h-full object-cover rounded-xl md:col-span-1" />
                            <div className="md:col-span-2">
                                <span className="text-xs uppercase tracking-wider text-blue-500 font-semibold">
                                    {booking.type === "cab" ? "Cab Booking" : "Tour Package"}
                                </span>
                                <h2 className="font-display text-2xl text-ink font-bold mt-1 leading-tight">
                                    {booking.cabName || booking.packageName}
                                </h2>
                                {booking.type === "cab" ? (
                                    <p className="text-sm text-ink-light mt-1 flex items-center gap-1">
                                        <MapPin className="w-3.5 h-3.5 text-blue-500" /> {booking.from} → {booking.to} · {booking.tripType}
                                    </p>
                                ) : (
                                    <p className="text-sm text-ink-light mt-1 flex items-center gap-1">
                                        <MapPin className="w-3.5 h-3.5 text-blue-500" /> Starting from {booking.startingCity}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm pt-5 border-t border-cream-200">
                            <Detail icon={Calendar} label={booking.type === "cab" ? "Pickup date" : "Start date"} value={formatDate(booking.date || booking.startDate)} />
                            {booking.returnDate && <Detail icon={Calendar} label="Return date" value={formatDate(booking.returnDate)} />}
                            <Detail icon={User} label="Lead traveller" value={booking.customer.name} />
                            <Detail icon={Phone} label="Phone" value={booking.customer.phone} />
                            <Detail icon={Mail} label="Email" value={booking.customer.email} />
                            <Detail label={booking.type === "cab" ? "Passengers" : "Travellers"} value={
                                booking.type === "cab"
                                    ? `${booking.passengers}`
                                    : `${booking.adults} adults · ${booking.children} kids`
                            } />
                            <Detail label="Booked on" value={formatDateTime(booking.createdAt)} />
                            <Detail label="Status" value={
                                <span className="inline-block px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold capitalize">
                                    {booking.status}
                                </span>
                            } />
                        </div>

                        <div className="flex items-center justify-between p-5 rounded-xl bg-blue-50 border border-blue-100">
                            <div>
                                <div className="text-xs uppercase tracking-wider text-blue-600 font-semibold">Amount payable</div>
                                <div className="font-display text-3xl font-bold text-blue-500">{formatCurrency(booking.totalAmount)}</div>
                            </div>
                            <div className="text-right text-xs text-ink-light">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-blue-600 font-semibold border border-blue-200">
                                    <Wallet className="w-3.5 h-3.5" /> Direct to Driver — pay on arrival
                                </span>
                            </div>
                        </div>

                        <a
                            href={whatsappLink}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-xl bg-[#25D366] hover:bg-[#1FB855] text-white font-semibold transition shadow-md"
                            data-testid="confirm-whatsapp"
                        >
                            <MessageCircle className="w-4 h-4" /> Send booking on WhatsApp · +91 80-75814740
                        </a>

                        <div className="flex flex-wrap gap-3 pt-4 print:hidden">
                            <button onClick={handlePrint} className="px-5 py-2.5 rounded-xl border border-cream-200 hover:border-blue-300 text-ink font-semibold text-sm flex items-center gap-2" data-testid="print-confirmation">
                                <Download className="w-4 h-4" /> Print
                            </button>
                            <button onClick={handleDownload} className="px-5 py-2.5 rounded-xl border border-cream-200 hover:border-blue-300 text-ink font-semibold text-sm flex items-center gap-2" data-testid="download-confirmation">
                                <Download className="w-4 h-4" /> Download
                            </button>
                            <Link to="/my-bookings" className="ml-auto px-5 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold text-sm flex items-center gap-2 transition">
                                My Bookings
                            </Link>
                            <Link to="/" className="px-5 py-2.5 rounded-xl border border-cream-200 hover:border-blue-300 text-ink font-semibold text-sm flex items-center gap-2">
                                <Home className="w-4 h-4" /> Home
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Detail = ({ icon: Icon, label, value }) => (
    <div>
        <div className="text-[10px] uppercase tracking-wider text-ink-light font-semibold flex items-center gap-1">
            {Icon && <Icon className="w-3 h-3" />} {label}
        </div>
        <div className="text-ink font-medium mt-0.5">{value || "—"}</div>
    </div>
);

export default BookingConfirmation;
